import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Award, MessageCircle, Mail, MapPin, Trophy, GraduationCap, Heart } from "lucide-react";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import WhatsAppButton from "@/components/landing/WhatsAppButton";
import { AUTHOR, AUTHOR_JSONLD } from "@/lib/author";

export default function AboutSaraiPage({ t, language, setLanguage }) {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <main className="relative bg-[#f9f6f0] text-[#1a2421]" data-testid="about-sarai-page">
      <Helmet>
        <title>Sarai Núñez · Distribuidora Immunotec en México | Eliora Longevity</title>
        <meta name="description" content="Conoce a Sarai Núñez: mamá de 5, empresaria, ex-nadadora de alto rendimiento, licenciada en Sistemas Computacionales. Distribuidora Independiente Immunotec con enfoque en medicina funcional y longevidad celular." />
        <link rel="canonical" href="https://elioralongevity.com/sobre-sarai" />
        <meta property="og:title" content="Sarai Núñez · Distribuidora Immunotec en México | Eliora Longevity" />
        <meta property="og:description" content="Mamá de 5, empresaria, ex-nadadora. Apasionada por la medicina funcional y la longevidad celular." />
        <meta property="og:url" content="https://elioralongevity.com/sobre-sarai" />
        <script type="application/ld+json">{JSON.stringify(AUTHOR_JSONLD)}</script>
      </Helmet>

      <Header t={t} language={language} setLanguage={setLanguage} />

      <section className="border-b border-[#2c4a3b]/8 py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <nav className="text-xs text-[#5c6b61] mb-6">
            <Link to="/" className="hover:text-[#bc6c25]">Inicio</Link> · <span className="text-[#1a2421]">Sobre Sarai</span>
          </nav>
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#bc6c25] font-bold">Sobre la fundadora</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-3 leading-[1.05]">
            Hola, soy <em className="text-[#bc6c25]">Sarai Núñez</em>
          </h1>
          <p className="mt-6 text-lg text-[#5c6b61] leading-relaxed">{AUTHOR.shortBio}</p>
        </div>
      </section>

      <article className="max-w-3xl mx-auto px-6 md:px-10 py-16 prose-article">
        <div className="grid sm:grid-cols-2 gap-4 not-prose mb-10">
          {[
            { icon: Heart, label: "Mamá de", value: "5 hijos" },
            { icon: Trophy, label: "Ex-nadadora", value: "Alto rendimiento" },
            { icon: GraduationCap, label: "Formación", value: "Lic. en Sistemas Computacionales" },
            { icon: Award, label: "Hoy", value: "Distribuidora Independiente Immunotec" },
          ].map((f, i) => (
            <div key={i} className="bg-white border border-[#2c4a3b]/8 rounded-2xl p-5 flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#bc6c25]/10 grid place-items-center text-[#bc6c25] flex-shrink-0">
                <f.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#5c6b61]">{f.label}</p>
                <p className="text-sm font-semibold text-[#1a2421] mt-0.5">{f.value}</p>
              </div>
            </div>
          ))}
        </div>

        <h2>Mi historia, en corto</h2>
        <p>Fui nadadora de alto rendimiento durante años. Esa etapa me enseñó algo que hoy es la base de todo lo que hago: <strong>tu cuerpo es tu vehículo y depende 100% de cómo lo cuides</strong>. Después llegó la maternidad — cinco hijos. La empresa. La vida real, con sus presiones y sus pausas pendientes.</p>
        <p>En algún momento, como tantas mujeres, sentí que la energía no me alcanzaba. Empecé a investigar qué le faltaba a mi cuerpo desde una mirada más profunda — no solo "dormir más" o "tomar vitaminas", sino entender <strong>la salud celular</strong>. Ahí descubrí la medicina funcional. Y ahí descubrí Immunotec.</p>

        <h2>Por qué Immunotec y por qué glutatión</h2>
        <p>Probé muchas cosas. Lo que me convenció de Immunotec fue su producto estrella: <Link to="/immunocal">Immunocal</Link>, una proteína aislada de suero patentada y respaldada por <strong>más de 30 años de investigación</strong> de la Universidad McGill (Canadá). Su efecto principal: <Link to="/glutation">elevar tus niveles intracelulares de glutatión</Link>, el antioxidante maestro.</p>
        <p>Cuando empecé a notar mis propios cambios — más energía sostenida, menos resfriados, recuperación más rápida — decidí compartirlo. Hoy ayudo a otras personas en México a recuperar lo mismo, con protocolos personalizados.</p>

        <h2>Mi filosofía</h2>
        <ul>
          <li><strong>Educación primero, venta después.</strong> Antes de recomendarte algo, te explico por qué.</li>
          <li><strong>Hábitos {">"} suplementos.</strong> Los suplementos son apoyo, no sustituto. Trabajamos también tu sueño, alimentación, manejo del estrés.</li>
          <li><strong>Acompañamiento real.</strong> No te dejo sola después de la compra. Sigo contigo durante los 3 meses del protocolo.</li>
          <li><strong>Ciencia, no promesas.</strong> Cito estudios y diferencio claramente lo que es soporte nutricional de lo que es tratamiento médico.</li>
        </ul>

        <h2>Áreas en las que se especializa el equipo con el que colaboro</h2>
        <p className="text-sm text-[#5c6b61] mb-2">
          Trabajo de la mano de un equipo liderado por un <strong>médico general</strong> de quien recibimos las principales capacitaciones y consejos. Estas son las áreas de especialización del equipo:
        </p>
        <ul>
          {AUTHOR.expertise.map((e, i) => <li key={i}>{e}</li>)}
        </ul>

        <div className="mt-10 not-prose bg-white border border-[#2c4a3b]/8 rounded-3xl p-7">
          <h3 className="font-serif text-2xl">Vamos a platicar</h3>
          <p className="text-sm text-[#5c6b61] mt-2">Si quieres conocer más o tienes dudas específicas, escríbeme directo:</p>
          <div className="mt-5 flex flex-col sm:flex-row gap-3">
            <a
              href={`https://wa.me/${AUTHOR.whatsapp.replace(/\D/g, "")}?text=Hola%20Sarai%2C%20vi%20tu%20p%C3%A1gina%20y%20quiero%20conocer%20m%C3%A1s%20%E2%9C%A8`}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white px-6 py-3.5 text-sm font-semibold"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
            <a
              href={`mailto:${AUTHOR.email}`}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-[#2c4a3b]/30 text-[#1a2421] px-6 py-3.5 text-sm font-semibold"
            >
              <Mail className="w-4 h-4" /> {AUTHOR.email}
            </a>
          </div>
          <p className="mt-4 text-xs text-[#5c6b61] inline-flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5" /> {AUTHOR.location}
          </p>
        </div>

        <p className="mt-12 text-xs text-[#5c6b61] italic">
          Aviso médico: la información en este sitio es educativa y no sustituye consulta médica profesional. Los productos Immunotec son suplementos alimenticios; no diagnostican, tratan, curan ni previenen enfermedades.
        </p>
      </article>

      <Footer t={t} />
      <WhatsAppButton label={t?.whatsapp} />
    </main>
  );
}
